import React, { useState, useEffect, useMemo } from 'react';
import { 
  Shield, 
  Search, 
  Filter, 
  Calendar, 
  User, 
  Clock, 
  FileText, 
  Eye, 
  AlertTriangle,
  Monitor,
  Download,
  CheckCircle2,
  XCircle,
  RefreshCw,
  MoreVertical,
  ChevronRight,
  ChevronDown,
  History
} from 'lucide-react';
import { collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  limit,
  Timestamp,
  startAt,
  endAt } from '../firebase';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { motion, AnimatePresence } from 'motion/react';
import { format } from 'date-fns';

interface ActivityLogViewProps {
  orgId: string;
  language?: string;
  initialTargetId?: string | null;
}

export default function ActivityLogView({ orgId, language = 'en', initialTargetId }: ActivityLogViewProps) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!orgId) return;

    const appAuthToken = localStorage.getItem('app_auth_token');
    if (appAuthToken) {
      const controller = new AbortController();

      const loadLocalActivityLogs = async () => {
        try {
          const parameters = new URLSearchParams();
          if (initialTargetId) parameters.set('targetId', initialTargetId);
          const queryString = parameters.toString();
          const response = await fetch(`/api/organizations/${orgId}/activity-logs${queryString ? `?${queryString}` : ''}`, {
            headers: { Authorization: `Bearer ${appAuthToken}` },
            signal: controller.signal
          });

          if (!response.ok) throw new Error(`Failed to load activity logs: ${response.status}`);

          const payload = await response.json();
          const rawLogs = Array.isArray(payload)
            ? payload
            : payload.activityLogs ?? payload.activity_logs ?? payload.logs ?? payload.data ?? [];

          const logsData = (Array.isArray(rawLogs) ? rawLogs : []).map((log: any, index: number) => {
            const rawTimestamp = log.timestamp ?? log.createdAt ?? log.created_at ?? log.loggedAt ?? log.logged_at;
            const timestamp = rawTimestamp?.toDate?.()
              ?? (rawTimestamp?.seconds ?? rawTimestamp?._seconds
                ? new Date((rawTimestamp.seconds ?? rawTimestamp._seconds) * 1000)
                : new Date(rawTimestamp ?? Date.now()));
            const metadata = log.metadata ?? log.metaData ?? log.meta_data ?? log.details ?? {};

            return {
              ...log,
              id: String(log.id ?? log._id ?? log.logId ?? log.log_id ?? index),
              userName: log.userName ?? log.user_name ?? log.actorName ?? log.actor_name ?? log.user?.name ?? log.actor?.name ?? '',
              userEmail: log.userEmail ?? log.user_email ?? log.actorEmail ?? log.actor_email ?? log.user?.email ?? log.actor?.email ?? '',
              action: log.action ?? log.actionType ?? log.action_type ?? log.eventType ?? log.event_type ?? '',
              module: log.module ?? log.moduleName ?? log.module_name ?? log.resourceType ?? log.resource_type ?? '',
              targetName: log.targetName ?? log.target_name ?? log.target?.name ?? log.entityName ?? log.entity_name ?? '',
              metadata: {
                ...metadata,
                before: metadata.before ?? metadata.beforeData ?? metadata.before_data,
                after: metadata.after ?? metadata.afterData ?? metadata.after_data,
                reason: metadata.reason ?? metadata.changeReason ?? metadata.change_reason,
                pageName: metadata.pageName ?? metadata.page_name,
                userAgent: metadata.userAgent ?? metadata.user_agent
              },
              timestamp: Number.isNaN(timestamp.getTime()) ? new Date() : timestamp
            };
          });

          if (!controller.signal.aborted) setLogs(logsData);
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Failed to load activity logs', error);
            setLogs([]);
          }
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      };

      loadLocalActivityLogs();
      return () => controller.abort();
    }

    const q = query(
      collection(db, 'activity_logs'),
      where('orgId', '==', orgId),
      orderBy('timestamp', 'desc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const logsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      setLogs(logsData);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'activity_logs');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [orgId, initialTargetId, refreshKey]);

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const matchesSearch = 
        log.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.targetName?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || log.action === filterType;
      const matchesModule = filterModule === 'all' || log.module === filterModule;
      
      const logDate = log.timestamp;
      const matchesStartDate = !dateRange.start || logDate >= new Date(dateRange.start);
      // End date sets to the end of the day
      const endDate = dateRange.end ? new Date(dateRange.end) : null;
      if (endDate) endDate.setHours(23, 59, 59, 999);
      const matchesEndDate = !endDate || logDate <= endDate;

      const matchesTarget = !initialTargetId || log.targetId === initialTargetId || log.target_id === initialTargetId;

      return matchesTarget && matchesSearch && matchesType && matchesModule && matchesStartDate && matchesEndDate;
    });
  }, [logs, searchTerm, filterType, filterModule, dateRange, initialTargetId]);

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE': return <PlusCircleIcon />;
      case 'EDIT': return <EditIcon />;
      case 'APPROVE': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'REVERSE_PAYMENT':
      case 'VOID': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'SCREENSHOT':
      case 'TAB_SWITCH': return <Monitor className="w-4 h-4 text-orange-500" />;
      default: return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  const PlusCircleIcon = () => (
    <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
      <div className="w-2 h-0.5 bg-green-600 rounded-full" />
      <div className="w-0.5 h-2 bg-green-600 rounded-full absolute" />
    </div>
  );

  const EditIcon = () => (
    <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center">
       <div className="w-2 h-2 border border-blue-600 rounded-sm" />
    </div>
  );

  const modules = Array.from(new Set(logs.map(l => l.module))).filter(Boolean);
  const actions = Array.from(new Set(logs.map(l => l.action))).filter(Boolean);

  return (
    <div className="flex flex-col h-full bg-[#F5F2F0] rounded-[40px] border border-black/5 overflow-hidden">
      {/* Header */}
      <div className="p-8 bg-white border-b border-black/5 shadow-sm">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-[#141414] text-[#E4E3E0] rounded-[24px] shadow-lg">
              <Shield className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold italic serif tracking-tight">{initialTargetId ? 'Transaction Action Log' : 'Audit Trail'}</h1>
              <p className="text-xs font-bold opacity-30 uppercase tracking-[0.2em] mt-1">{initialTargetId ? `Filtered to transaction ${initialTargetId}` : 'Immutable Security Logs & Action Tracking'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
             <div className="px-4 py-2 bg-green-50 text-green-600 rounded-full text-xs font-bold flex items-center gap-2 border border-green-100">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live Monitoring Active
             </div>
             <button type="button" onClick={() => setRefreshKey(value => value + 1)} className="p-3 hover:bg-black/5 rounded-full transition-all" aria-label="Refresh activity log">
                <RefreshCw className="w-5 h-5 opacity-40" />
             </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30" />
            <input 
              type="text"
              placeholder="Search user, action, target..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-black/5 border-none rounded-2xl text-sm focus:ring-2 ring-[#141414]/10 transition-all font-medium"
            />
          </div>
          
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30" />
              <select 
                value={filterModule}
                onChange={(e) => setFilterModule(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-black/5 border-none rounded-2xl text-sm focus:ring-2 ring-[#141414]/10 transition-all font-medium appearance-none"
              >
                <option value="all">All Modules</option>
                {modules.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className="relative">
            <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30" />
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-black/5 border-none rounded-2xl text-sm focus:ring-2 ring-[#141414]/10 transition-all font-medium appearance-none"
            >
              <option value="all">All Actions</option>
              {actions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div className="flex gap-2">
             <input 
               type="date"
               value={dateRange.start}
               onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
               className="flex-1 px-4 py-3 bg-black/5 border-none rounded-2xl text-xs focus:ring-2 ring-[#141414]/10 font-bold"
             />
             <input 
               type="date"
               value={dateRange.end}
               onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
               className="flex-1 px-4 py-3 bg-black/5 border-none rounded-2xl text-xs focus:ring-2 ring-[#141414]/10 font-bold"
             />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full opacity-20">
             <RefreshCw className="w-12 h-12 animate-spin mb-4" />
             <p className="font-bold serif italic">Decrypting Secure Logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full opacity-20">
             <History className="w-16 h-16 mb-4" />
             <p className="font-bold serif italic">No forensic data found matching your query.</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-6 top-4 bottom-4 w-px bg-black/10" />

            <div className="space-y-8">
              {filteredLogs.map((log) => {
                const isExpanded = expandedLogId === log.id;
                const isSecurity = log.module === 'Security';
                
                return (
                  <div key={log.id} className="relative pl-16 group">
                    {/* Action Icon Node */}
                    <div className={cn(
                      "absolute left-0 top-1 w-12 h-12 rounded-full border-2 flex items-center justify-center z-10 transition-all shadow-sm",
                      isSecurity ? "bg-orange-50 border-orange-200" : "bg-white border-black/10 group-hover:border-black group-hover:scale-110"
                    )}>
                      {getActionIcon(log.action)}
                    </div>

                    <div className={cn(
                      "p-6 rounded-[32px] border transition-all",
                      isSecurity ? "bg-orange-50/30 border-orange-200/50" : "bg-white border-black/5 hover:border-black/10 hover:shadow-lg"
                    )}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-black uppercase tracking-widest">{log.action}</span>
                            <span className="text-[10px] uppercase font-bold opacity-30 tracking-[0.2em]">{log.module}</span>
                            {log.targetName && (
                              <div className="px-2 py-0.5 bg-black/5 rounded text-[10px] font-bold">
                                {log.targetName}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-3 text-sm font-medium">
                            <span className="text-[#141414]">{log.userName}</span>
                            <span className="opacity-20">•</span>
                            <span className="opacity-40">{log.userEmail}</span>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-xs font-bold opacity-30 uppercase tracking-widest">
                            {format(log.timestamp, 'HH:mm:ss')}
                          </div>
                          <div className="text-[10px] font-bold opacity-20 uppercase mt-0.5">
                            {format(log.timestamp, 'dd MMM yyyy')}
                          </div>
                        </div>
                      </div>

                      {/* Summary Section */}
                      <div className="mt-4 flex items-center gap-4">
                        <button 
                          onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] opacity-40 hover:opacity-100 transition-all"
                        >
                          {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          View Forensic Detail
                        </button>
                        
                        {(log.metadata?.before || log.metadata?.after) && (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-black uppercase tracking-wider">
                            Delta Logged
                          </span>
                        )}

                        {log.metadata?.reason && (
                           <p className="text-xs font-bold italic opacity-60">"{log.metadata.reason}"</p>
                        )}
                      </div>

                      {/* Detail Expansion */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-6 pt-6 border-t border-black/5 grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Metadata Items */}
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-[10px] font-black uppercase tracking-widest opacity-30 mb-2">Contextual Data</h4>
                                  <div className="space-y-2">
                                     {log.metadata?.pageName && (
                                       <div className="flex items-center gap-2 text-xs">
                                          <Monitor className="w-3.5 h-3.5 opacity-30" />
                                          <span className="font-bold opacity-60">Page:</span>
                                          <span className="font-medium bg-black/5 px-2 rounded">{log.metadata.pageName}</span>
                                       </div>
                                     )}
                                     {log.metadata?.userAgent && (
                                       <div className="flex items-start gap-2 text-[10px]">
                                          <div className="mt-1 font-bold opacity-30">UA:</div>
                                          <span className="opacity-40 italic break-all leading-tight">{log.metadata.userAgent}</span>
                                       </div>
                                     )}
                                  </div>
                                </div>
                              </div>

                              {/* Diff View */}
                              {(log.metadata?.before || log.metadata?.after) && (
                                <div>
                                   <h4 className="text-[10px] font-black uppercase tracking-widest opacity-30 mb-2">Data Transition (JSON Delta)</h4>
                                   <div className="bg-black/5 rounded-2xl p-4 font-mono text-[10px] max-h-[300px] overflow-auto custom-scrollbar space-y-2">
                                      {log.metadata.before && (
                                        <div className="space-y-1">
                                          <div className="text-red-500 font-bold tracking-widest uppercase">[-] DELETED/ORIGINAL</div>
                                          <pre className="opacity-60">{JSON.stringify(log.metadata.before, null, 2)}</pre>
                                        </div>
                                      )}
                                      {log.metadata.after && (
                                        <div className="space-y-1">
                                          <div className="text-green-600 font-bold tracking-widest uppercase">[+] ADDED/MODIFIED</div>
                                          <pre className="opacity-80">{JSON.stringify(log.metadata.after, null, 2)}</pre>
                                        </div>
                                      )}
                                   </div>
                                </div>
                              )}
                            </div>

                            <div className="mt-6 flex justify-end">
                               <button className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-xl hover:scale-105 active:scale-95 transition-all">
                                  <Download className="w-3.5 h-3.5" />
                                  Export as Signed JSON
                               </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer / Stats */}
      <div className="p-6 bg-white border-t border-black/5 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.2em] opacity-40">
        <div className="flex gap-4">
           <span>Total Logs: {filteredLogs.length}</span>
           <span>Filtered View</span>
        </div>
        <div className="flex items-center gap-2">
           <Shield className="w-3 h-3" />
           <span>GDPR & ISO-27001 Compliant Audit Engine</span>
        </div>
      </div>
    </div>
  );
}

function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(' ');
}
